import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, FileText, Send, Inbox, UserX, PlayCircle, PauseCircle, XCircle, CheckCircle2, AlertTriangle, Trash2, Zap, Plus, Clock, Users, Search, MessageSquare, GripVertical, FolderOpen, ChevronDown, Pencil } from "lucide-react";
import useListDnd from "@/lib/useListDnd";
import VoiceRecorder from "@/components/VoiceRecorder";
import VoicePlayer from "@/components/VoicePlayer";

export default function AdminWhatsApp() {
  const [tab, setTab] = useState("status");
  return (
    <div className="space-y-4" data-testid="admin-whatsapp">
      <div className="flex items-center gap-3">
        <MessageCircle className="h-6 w-6 text-green-700" />
        <h1 className="text-2xl sm:text-3xl font-display font-semibold">واتساب</h1>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="wa-tabs" className="flex-wrap">
          <TabsTrigger value="status" data-testid="wa-tab-status">الحالة</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="wa-tab-inbox"><Inbox className="h-3 w-3 me-1" />المحادثات</TabsTrigger>
          <TabsTrigger value="templates" data-testid="wa-tab-templates">القوالب</TabsTrigger>
          <TabsTrigger value="automations" data-testid="wa-tab-automations"><Zap className="h-3 w-3 me-1" />التلقائيات</TabsTrigger>
          <TabsTrigger value="broadcasts" data-testid="wa-tab-broadcasts">الحملات</TabsTrigger>
          <TabsTrigger value="logs" data-testid="wa-tab-logs">سجل الرسائل</TabsTrigger>
          <TabsTrigger value="unsubs" data-testid="wa-tab-unsubs">الملغى اشتراكهم</TabsTrigger>
        </TabsList>
        <TabsContent value="status"><WAStatus /></TabsContent>
        <TabsContent value="inbox"><WAInbox /></TabsContent>
        <TabsContent value="templates"><WATemplates /></TabsContent>
        <TabsContent value="automations"><WAAutomations /></TabsContent>
        <TabsContent value="broadcasts"><WABroadcasts /></TabsContent>
        <TabsContent value="logs"><WALogs /></TabsContent>
        <TabsContent value="unsubs"><WAUnsubs /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------- Status ----------------
function WAStatus() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/whatsapp/status").then((r) => setStatus(r.data));
  }, []);
  if (!status) return <p className="text-foreground/50 py-6">جاري التحميل...</p>;
  return (
    <div className="space-y-3 pt-2">
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          {status.configured ? (
            <><CheckCircle2 className="h-5 w-5 text-green-700" /><span className="font-semibold text-green-800">Twilio متصل — الوضع الحيّ</span></>
          ) : (
            <><AlertTriangle className="h-5 w-5 text-amber-600" /><span className="font-semibold text-amber-700">وضع المحاكاة — لم يتم إعداد Twilio بعد</span></>
          )}
        </div>
        <div className="text-sm text-foreground/70 space-y-1">
          <div>الحساب: {status.account_sid_last4 ? `••••${status.account_sid_last4}` : "غير محدد"}</div>
          <div>الرقم المرسل: <span dir="ltr">{status.from_number || "غير محدد"}</span></div>
        </div>
        {!status.configured && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 space-y-2">
            <div className="font-semibold">لتفعيل الإرسال الحيّ، أضف هذه المتغيرات في لوحة النشر (Deployment env):</div>
            <ul className="list-disc list-inside space-y-1 font-mono">
              <li>TWILIO_ACCOUNT_SID</li>
              <li>TWILIO_AUTH_TOKEN</li>
              <li>TWILIO_WHATSAPP_FROM (مثال: whatsapp:+14155238886)</li>
              <li>TWILIO_MESSAGING_SERVICE_SID (اختياري)</li>
            </ul>
            <div>حاليًا، يتم تسجيل جميع الرسائل في سجل الرسائل كـ "mock" بدون إرسال حقيقي — يمكنك اختبار الواجهة والحملات بأمان.</div>
          </div>
        )}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 space-y-2">
          <div className="font-semibold">Webhooks لإدخالها في Twilio Console:</div>
          <ul className="space-y-1 font-mono">
            <li>Status callback: <span className="break-all">{window.location.origin}/api/webhooks/twilio/status</span></li>
            <li>Inbound (رد المستخدم): <span className="break-all">{window.location.origin}/api/webhooks/twilio/inbound</span></li>
          </ul>
        </div>
      </Card>
    </div>
  );
}

// ---------------- Templates ----------------
function WATemplates() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [testTarget, setTestTarget] = useState(null);
  const [folders, setFolders] = useState([]);
  const [collapsedFolders, setCollapsedFolders] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("admin_wa_folders_collapsed") || "[]")); }
    catch { return new Set(); }
  });

  const load = async () => {
    const [t, f] = await Promise.all([
      api.get("/whatsapp/templates"),
      api.get("/whatsapp/folders").catch(() => ({ data: [] })),
    ]);
    setTemplates(t.data);
    setFolders(f.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put(`/whatsapp/templates/${editing.key}`, editing);
      toast.success("تم الحفظ");
      setEditing(null);
      load();
    } catch (err) { toast.error("فشل الحفظ: " + (err?.response?.data?.detail || "خطأ")); }
  };

  const del = async (key) => {
    if (!confirm(`حذف القالب "${key}"؟`)) return;
    await api.delete(`/whatsapp/templates/${key}`);
    toast.success("تم الحذف");
    load();
  };

  const newTemplate = () => setEditing({
    key: "", name: "", description: "", body: "", content_sid: "",
    variables_hint: "", sample_variables: {}, disabled: false, folder: "", _isNew: true,
  });

  // Persist a new drag-drop order to the backend (optimistic update + revert on failure)
  const persistOrder = async (newList, meta) => {
    const movedItem = meta?.movedItem;
    const targetItem = meta?.targetItem;
    // Cross-folder drop → change moved item's folder to match target's.
    if (movedItem && targetItem && (movedItem.folder || "") !== (targetItem.folder || "")) {
      const newFolder = targetItem.folder || "";
      newList = newList.map((t) => t.key === movedItem.key ? { ...t, folder: newFolder } : t);
    }
    setTemplates(newList);
    try {
      if (movedItem && targetItem && (movedItem.folder || "") !== (targetItem.folder || "")) {
        await api.put("/whatsapp/templates/move-to-folder", {
          keys: [movedItem.key],
          folder: targetItem.folder || "",
        });
      }
      await api.put("/whatsapp/templates/reorder", { keys: newList.map((t) => t.key) });
    } catch (e) {
      toast.error("فشل حفظ الترتيب");
      load();
    }
  };
  const { getItemProps } = useListDnd({
    items: templates,
    getId: (t) => t.key,
    onReorder: persistOrder,
  });

  // Drop onto a folder header → move item into that folder
  const folderDropProps = (folderName) => ({
    onDragOver: (e) => { e.preventDefault(); },
    onDrop: async (e) => {
      e.preventDefault();
      const key = e.dataTransfer.getData("text/plain");
      if (!key) return;
      const item = templates.find((t) => t.key === key);
      if (!item || (item.folder || "") === folderName) return;
      const next = templates.map((t) => t.key === key ? { ...t, folder: folderName } : t);
      setTemplates(next);
      try {
        await api.put("/whatsapp/templates/move-to-folder", { keys: [key], folder: folderName });
        toast.success(folderName ? `نُقل إلى: ${folderName}` : "نُقل إلى: غير مصنّف");
        load();
      } catch (err) { toast.error("فشل النقل"); load(); }
    },
  });

  const toggleFolder = (name) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      try { localStorage.setItem("admin_wa_folders_collapsed", JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const renameFolder = async (oldName) => {
    const newName = window.prompt("اسم المجلد الجديد (اتركه فارغًا لإفراغ المجلد)", oldName);
    if (newName === null) return;
    if (newName.trim() === oldName) return;
    try {
      await api.put("/whatsapp/folders/rename", { old_name: oldName, new_name: newName.trim() });
      toast.success("تم تحديث المجلد");
      load();
    } catch (e) { toast.error("فشل إعادة التسمية"); }
  };

  const grouped = templates.reduce((acc, t) => {
    const f = t.folder || "";
    (acc[f] = acc[f] || []).push(t);
    return acc;
  }, {});
  const folderOrder = folders.length > 0
    ? folders.map((f) => f.name)
    : [...new Set(templates.map((t) => t.folder || ""))].sort((a, b) => (a === "") - (b === "") || a.localeCompare(b));

  return (
    <div className="space-y-3 pt-2">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-sm text-foreground/60">
          القوالب مطلوبة من Meta لإرسال الرسائل للعملاء الذين لم يبدأوا محادثة. أنشئها في Twilio Console ثم أضف ContentSid هنا.
        </p>
        <Button onClick={newTemplate} data-testid="wa-new-template">قالب جديد</Button>
      </div>
      <div className="text-[10px] text-foreground/50 flex items-center gap-1 pt-1" data-testid="wa-tpl-list-hint">
        <GripVertical className="h-3 w-3" /> اسحب من المقبض لإعادة ترتيب القوالب — استخدم المجلدات لتنظيمها في مجموعات
      </div>
      <div className="space-y-3">
        {folderOrder.map((folderName) => {
          const items = grouped[folderName] || [];
          if (items.length === 0) return null;
          const isCollapsed = collapsedFolders.has(folderName);
          const label = folderName || "غير مصنّف";
          return (
            <div key={folderName || "__uncat__"} data-testid={`wa-folder-${folderName || "uncat"}`}>
              <div
                className="flex items-center gap-1 pb-1 border-b border-foreground/10 group rounded transition-colors [&.folder-dropzone-active]:bg-primary/15"
                {...folderDropProps(folderName)}
                onDragEnter={(e) => e.currentTarget.classList.add("folder-dropzone-active")}
                onDragLeave={(e) => e.currentTarget.classList.remove("folder-dropzone-active")}
                onDropCapture={(e) => e.currentTarget.classList.remove("folder-dropzone-active")}
                data-testid={`wa-folder-dropzone-${folderName || "uncat"}`}
              >
                <button
                  type="button"
                  onClick={() => toggleFolder(folderName)}
                  className="flex-1 flex items-center gap-1.5 text-xs font-semibold text-foreground/60 hover:text-foreground text-right"
                  data-testid={`wa-folder-toggle-${folderName || "uncat"}`}
                >
                  <FolderOpen className="h-3.5 w-3.5" />
                  <span>{label}</span>
                  <span className="text-[10px] opacity-60">({items.length})</span>
                  <ChevronDown className={`h-3 w-3 me-auto transition-transform ${isCollapsed ? "-rotate-90 rtl:rotate-90" : ""}`} />
                </button>
                {folderName && (
                  <button
                    type="button"
                    onClick={() => renameFolder(folderName)}
                    className="opacity-0 group-hover:opacity-70 hover:!opacity-100 p-0.5 text-foreground/60"
                    title="إعادة تسمية"
                    data-testid={`wa-folder-rename-${folderName}`}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
              {!isCollapsed && (
                <div className="grid gap-2 mt-2">
                  {items.map((t) => (
                    <Card
                      key={t.key}
                      {...getItemProps(t)}
                      className="p-4 data-[dragging=true]:opacity-40 data-[dragover=true]:ring-2 data-[dragover=true]:ring-primary/60 transition"
                      data-testid={`wa-tpl-${t.key}`}
                    >
                      <div className="flex justify-between flex-wrap gap-2">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <div
                            className="cursor-grab active:cursor-grabbing touch-none text-foreground/30 hover:text-foreground pt-0.5"
                            title="اسحب لإعادة الترتيب"
                            data-testid={`wa-drag-${t.key}`}
                          >
                            <GripVertical className="h-4 w-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold">{t.name}</span>
                              <Badge variant="secondary" className="text-[10px] font-mono" title="المُعرِّف الفني — لا يمكن تغييره">
                                <span className="opacity-60 me-1">ID:</span>{t.key}
                              </Badge>
                              {t.disabled && <Badge variant="destructive" className="text-[10px]">معطّل</Badge>}
                              {t.content_sid ? <Badge className="text-[10px] bg-green-100 text-green-800">مربوط بـ Twilio</Badge> : <Badge className="text-[10px] bg-amber-100 text-amber-800">ContentSid ناقص</Badge>}
                            </div>
                            <p className="text-xs text-foreground/60 mt-1">{t.description}</p>
                            <pre className="bg-foreground/5 rounded p-2 mt-2 text-xs whitespace-pre-wrap font-sans">{t.body}</pre>
                            <p className="text-[10px] text-foreground/50 mt-1">{t.variables_hint}</p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button size="sm" variant="outline" onClick={() => setEditing({ ...t, _isNew: false })} data-testid={`wa-edit-${t.key}`}>تعديل</Button>
                          <Button size="sm" variant="outline" onClick={() => setTestTarget(t)} data-testid={`wa-test-${t.key}`}>اختبار</Button>
                          <Button size="sm" variant="ghost" onClick={() => del(t.key)} data-testid={`wa-del-${t.key}`}><Trash2 className="h-3 w-3 text-red-600" /></Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
          {editing && (
            <>
              <DialogHeader><DialogTitle>{editing._isNew ? "قالب واتساب جديد" : `تعديل: ${editing.name}`}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium">
                    المُعرِّف الفني (Key) — <span className="text-foreground/50">لا يمكن تغييره بعد الإنشاء</span>
                  </label>
                  <Input
                    value={editing.key}
                    onChange={(e) => setEditing({ ...editing, key: e.target.value })}
                    disabled={!editing._isNew}
                    className={!editing._isNew ? "bg-muted/60 text-foreground/60 cursor-not-allowed" : ""}
                    data-testid="wa-tpl-key"
                  />
                  {!editing._isNew && (
                    <div className="text-[10px] text-foreground/50 mt-1 leading-relaxed">
                      المُعرِّف مثل اسم الملف — يستخدمه النظام في السيكوينسات والجداول المُبرمجة والإحصائيات. تغييره سيكسر كل الروابط.
                      لتغيير الاسم الظاهر في القائمة، عدّل خانة "الاسم الظاهر" أدناه.
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium">
                    الاسم الظاهر <span className="text-foreground/50">— هذا اللي يظهر في القوائم ولوحة الإدارة</span>
                  </label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="wa-tpl-name" />
                </div>
                <div>
                  <label className="text-xs font-medium">الوصف</label>
                  <Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-medium flex items-center gap-1"><FolderOpen className="h-3 w-3" /> المجلد (اختياري)</label>
                  <Input
                    list="wa-folder-list"
                    value={editing.folder || ""}
                    onChange={(e) => setEditing({ ...editing, folder: e.target.value })}
                    placeholder="مثال: مصادقة، تسويق، إشعارات..."
                    data-testid="wa-tpl-folder"
                  />
                  <datalist id="wa-folder-list">
                    {folders.map((f) => f.name && <option key={f.name} value={f.name} />)}
                  </datalist>
                  <p className="text-[10px] text-foreground/50 mt-1">اترك فارغًا لوضع القالب في "غير مصنّف".</p>
                </div>
                <div>
                  <label className="text-xs font-medium">Twilio ContentSid (HX...) — من Twilio Console بعد اعتماد Meta</label>
                  <Input value={editing.content_sid || ""} onChange={(e) => setEditing({ ...editing, content_sid: e.target.value })} placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxx" data-testid="wa-tpl-content-sid" />
                  <p className="text-[10px] text-foreground/50 mt-1">إذا فارغ: الرسالة تُرسل كنص حرّ (يعمل فقط داخل نافذة 24 ساعة).</p>
                </div>
                <div>
                  <label className="text-xs font-medium">نص الرسالة (للمعاينة والوضع الحرّ)</label>
                  <textarea
                    value={editing.body}
                    onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                    className="w-full border rounded-md p-2 text-sm min-h-[100px]"
                    dir="rtl"
                    data-testid="wa-tpl-body"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium">تلميح المتغيرات (مثال: 1=الاسم، 2=الرابط)</label>
                  <Input value={editing.variables_hint || ""} onChange={(e) => setEditing({ ...editing, variables_hint: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={editing.disabled || false} onChange={(e) => setEditing({ ...editing, disabled: e.target.checked })} data-testid="wa-tpl-disabled" />
                  معطّل (لا يُرسل)
                </label>
                <Button onClick={save} className="w-full" data-testid="wa-tpl-save">حفظ</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <SendTestDialog target={testTarget} onClose={() => setTestTarget(null)} />
    </div>
  );
}

function SendTestDialog({ target, onClose }) {
  const [to, setTo] = useState("");
  const [vars, setVars] = useState("{}");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (target) {
      setTo("");
      setResult(null);
      setVars(JSON.stringify(target.sample_variables || {}, null, 2));
    }
  }, [target]);

  const send = async () => {
    try {
      const variables = JSON.parse(vars || "{}");
      const { data } = await api.post("/whatsapp/send-test", { template_key: target.key, to, variables });
      setResult(data);
      toast[data.ok ? "success" : "error"](data.ok ? "تم الإرسال" : `فشل: ${data.result}`);
    } catch (err) {
      toast.error("خطأ: " + (err?.response?.data?.detail || err.message));
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-md">
        {target && <>
          <DialogHeader><DialogTitle>اختبار: {target.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">رقم الواتساب (E.164)</label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+966501234567" dir="ltr" data-testid="wa-test-to" />
            </div>
            <div>
              <label className="text-xs font-medium">المتغيرات (JSON)</label>
              <textarea value={vars} onChange={(e) => setVars(e.target.value)} className="w-full border rounded-md p-2 font-mono text-xs min-h-[100px]" dir="ltr" data-testid="wa-test-vars" />
            </div>
            <Button onClick={send} className="w-full" data-testid="wa-test-send">إرسال</Button>
            {result && (
              <div className={`text-xs p-2 rounded border ${result.ok ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                نتيجة: <span className="font-mono">{String(result.result)}</span>
              </div>
            )}
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Broadcasts ----------------
function WABroadcasts() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/whatsapp/broadcasts").then((r) => setBroadcasts(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = async (b) => {
    const { data } = await api.get(`/whatsapp/broadcasts/${b.id}`);
    setDetail(data);
  };

  const doAction = async (id, action) => {
    if (action === "cancel" && !confirm("إلغاء جميع الرسائل المعلقة؟")) return;
    await api.post(`/whatsapp/broadcasts/${id}/${action}`);
    toast.success("تم");
    if (detail) openDetail({ id });
    load();
  };

  const dateShort = (iso) => iso ? new Date(iso).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <div className="space-y-3 pt-2">
      <div className="flex justify-between">
        <p className="text-sm text-foreground/60">أرسل رسالة واتساب لجمهور محدد بمعدل يومي.</p>
        <Button onClick={() => setShowNew(true)} data-testid="wa-new-broadcast">حملة جديدة</Button>
      </div>
      {broadcasts.length === 0 && <Card className="p-8 text-center text-foreground/50">لا حملات بعد.</Card>}
      {broadcasts.map((b) => {
        const total = (b.sent_count || 0) + (b.pending_count || 0) + (b.failed_count || 0);
        const pct = total ? Math.round(((b.sent_count || 0) / total) * 100) : 0;
        return (
          <Card key={b.id} className="p-4 cursor-pointer hover:bg-black/[0.02]" onClick={() => openDetail(b)} data-testid={`wa-b-${b.id}`}>
            <div className="flex justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold">{b.name}</div>
                <div className="text-xs text-foreground/60">{b.template_key} • {b.audience} • {b.daily_rate}/يوم • {dateShort(b.created_at)}</div>
              </div>
              <div className="text-xs text-left space-y-1">
                <div className="flex gap-2">
                  <span className="text-green-700">✓ {b.sent_count || 0}</span>
                  <span className="text-yellow-700">⏳ {b.pending_count || 0}</span>
                  {b.failed_count > 0 && <span className="text-red-700">✗ {b.failed_count}</span>}
                </div>
                <Badge variant="secondary" className="text-[10px]">{b.status}</Badge>
              </div>
            </div>
            <div className="mt-3 h-2 bg-foreground/10 rounded-full overflow-hidden">
              <div className="h-full bg-green-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </Card>
        );
      })}

      <NewBroadcastDialog open={showNew} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
          {detail && <>
            <DialogHeader><DialogTitle>{detail.broadcast.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2 text-sm">
                <MiniStat label="إجمالي" value={detail.counts.total} />
                <MiniStat label="أُرسلت" value={detail.counts.sent} color="text-green-700" />
                <MiniStat label="معلقة" value={detail.counts.pending} color="text-yellow-700" />
                <MiniStat label="فشل" value={detail.counts.failed} color="text-red-700" />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => doAction(detail.broadcast.id, "pause")} data-testid="wa-b-pause"><PauseCircle className="h-4 w-4 me-1" />إيقاف</Button>
                <Button size="sm" variant="outline" onClick={() => doAction(detail.broadcast.id, "resume")} data-testid="wa-b-resume"><PlayCircle className="h-4 w-4 me-1" />استئناف</Button>
                <Button size="sm" variant="destructive" onClick={() => doAction(detail.broadcast.id, "cancel")} data-testid="wa-b-cancel"><XCircle className="h-4 w-4 me-1" />إلغاء</Button>
              </div>
              <div className="text-xs">
                <div className="text-foreground/60 mb-1">أول 50 رسالة:</div>
                <div className="border rounded-md divide-y max-h-80 overflow-y-auto">
                  {detail.queue.slice(0, 50).map((q, i) => (
                    <div key={i} className="p-2 flex justify-between gap-3">
                      <span className="font-mono truncate flex-1" dir="ltr">{q.to}</span>
                      <span className="text-foreground/50 text-[10px]">{dateShort(q.scheduled_at)}</span>
                      <Badge variant="secondary" className="text-[10px]">{q.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewBroadcastDialog({ open, onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [form, setForm] = useState({
    name: "", template_key: "", audience: "leads_not_purchased",
    daily_rate: 50, variables_map: { "1": "full_name" }, static_variables: {},
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/whatsapp/templates").then((r) => setTemplates(r.data.filter((t) => !t.disabled)));
      setAudiencePreview(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && form.audience) {
      api.get(`/whatsapp/audience/preview?audience=${form.audience}`).then((r) => setAudiencePreview(r.data));
    }
  }, [form.audience, open]);

  const currentTpl = templates.find((t) => t.key === form.template_key);

  const submit = async () => {
    if (!form.name || !form.template_key) return toast.error("أكمل الحقول المطلوبة");
    setSubmitting(true);
    try {
      await api.post("/whatsapp/broadcasts", form);
      toast.success("تم إنشاء الحملة");
      onCreated();
    } catch (err) {
      toast.error("فشل: " + (err?.response?.data?.detail || "خطأ"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" dir="rtl">
        <DialogHeader><DialogTitle>حملة واتساب جديدة</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">اسم الحملة</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wa-b-name" />
          </div>
          <div>
            <label className="text-xs font-medium">القالب</label>
            <select
              value={form.template_key}
              onChange={(e) => setForm({ ...form, template_key: e.target.value })}
              className="w-full border rounded-md p-2 text-sm bg-white"
              data-testid="wa-b-template"
            >
              <option value="">— اختر قالبًا —</option>
              {templates.map((t) => <option key={t.key} value={t.key}>{t.name} ({t.key})</option>)}
            </select>
            {currentTpl && (
              <pre className="bg-foreground/5 rounded p-2 mt-2 text-xs whitespace-pre-wrap font-sans">{currentTpl.body}</pre>
            )}
          </div>
          <div>
            <label className="text-xs font-medium">الجمهور</label>
            <select
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              className="w-full border rounded-md p-2 text-sm bg-white"
              data-testid="wa-b-audience"
            >
              <option value="leads_not_purchased">العملاء المحتملون الذين لم يشتروا</option>
              <option value="leads_all">جميع العملاء المحتملين</option>
              <option value="users_all">جميع الأعضاء</option>
            </select>
            {audiencePreview && (
              <p className="text-[10px] text-foreground/60 mt-1">
                🎯 {audiencePreview.eligible} مستلم مؤهل ({audiencePreview.total} إجمالي، {audiencePreview.opted_out} ملغى)
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium">المعدل اليومي</label>
            <div className="flex gap-2 flex-wrap">
              {[25, 50, 100, 200, 500].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, daily_rate: n })}
                  className={`px-3 py-1 rounded-full text-sm border ${form.daily_rate === n ? "bg-black text-white border-foreground" : "bg-white border-foreground/20"}`}
                  data-testid={`wa-rate-${n}`}
                >
                  {n}
                </button>
              ))}
              <Input type="number" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: parseInt(e.target.value) || 1 })} className="w-20" min={1} max={1000} />
            </div>
            {audiencePreview && (
              <p className="text-[10px] text-foreground/60 mt-1">
                ⏱️ سيستغرق حوالي {Math.ceil((audiencePreview.eligible || 0) / form.daily_rate)} يومًا
              </p>
            )}
          </div>
          <div>
            <label className="text-xs font-medium">ربط المتغيرات (JSON: {"{"}1: "full_name"{"}"})</label>
            <textarea
              value={JSON.stringify(form.variables_map, null, 2)}
              onChange={(e) => {
                try { setForm({ ...form, variables_map: JSON.parse(e.target.value) }); } catch {}
              }}
              className="w-full border rounded-md p-2 font-mono text-xs min-h-[60px]"
              dir="ltr"
              data-testid="wa-b-vars-map"
            />
            <p className="text-[10px] text-foreground/50">القيم المسموحة: full_name، email</p>
          </div>
          <div>
            <label className="text-xs font-medium">القيم الثابتة (JSON: {"{"}2: "https://..."{"}"})</label>
            <textarea
              value={JSON.stringify(form.static_variables, null, 2)}
              onChange={(e) => {
                try { setForm({ ...form, static_variables: JSON.parse(e.target.value) }); } catch {}
              }}
              className="w-full border rounded-md p-2 font-mono text-xs min-h-[60px]"
              dir="ltr"
              data-testid="wa-b-vars-static"
            />
          </div>
          <Button onClick={submit} disabled={submitting} className="w-full" data-testid="wa-b-submit">
            {submitting ? "جاري الإنشاء..." : "إنشاء الحملة"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Logs ----------------
function WALogs() {
  const [logs, setLogs] = useState([]);
  const [health, setHealth] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [since, setSince] = useState("");

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    if (since) params.since = new Date(since).toISOString();
    return Promise.all([
      api.get("/whatsapp/logs/health"),
      api.get("/whatsapp/logs", { params }),
    ]).then(([h, l]) => { setHealth(h.data); setLogs(l.data); });
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [q, status, since]);

  const chip = (state) => {
    if (state === "read") return <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-100 text-teal-800">قُرئت ✓✓</span>;
    if (state === "delivered") return <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">وصلت ✓</span>;
    if (state === "sent") return <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">أُرسلت</span>;
    return null;
  };

  return (
    <div className="space-y-3 pt-2">
      {/* Health pills */}
      {health && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="wa-log-health">
          <HealthCell label="قيد الانتظار" value={health.pending} color="bg-yellow-50 text-yellow-900 border-yellow-200" />
          <HealthCell label="أُرسلت (24h)" value={health.sent_24h} color="bg-slate-50 text-slate-800 border-slate-200" />
          <HealthCell label="وصلت (24h)" value={health.delivered_24h} color="bg-blue-50 text-blue-800 border-blue-200" />
          <HealthCell label="قُرئت (24h)" value={health.read_24h} color="bg-teal-50 text-teal-800 border-teal-200" />
          <HealthCell label="فشلت (24h)" value={health.failed_24h} color="bg-red-50 text-red-800 border-red-200" />
          <HealthCell label="معدل النجاح" value={`${health.success_rate_pct}%`} color={health.alert ? "bg-red-50 text-red-800 border-red-200" : "bg-green-50 text-green-800 border-green-200"} />
        </div>
      )}
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالرقم أو القالب..." dir="ltr" className="max-w-xs" data-testid="wa-log-search"
               onKeyDown={(e) => e.key === "Enter" && load()} />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded px-2 py-1.5 text-xs bg-white h-9" data-testid="wa-log-status">
          <option value="">كل الحالات</option>
          <option value="sent">أُرسلت</option>
          <option value="failed">فشلت</option>
        </select>
        <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="w-40 text-xs" data-testid="wa-log-since" />
        <Button size="sm" onClick={load}>بحث</Button>
        {(q || status || since) && (
          <button onClick={() => { setQ(""); setStatus(""); setSince(""); setTimeout(load, 50); }} className="text-xs text-foreground/60 underline">مسح</button>
        )}
        <span className="text-xs text-foreground/50 ms-auto">{logs.length} نتيجة</span>
      </div>
      <div className="space-y-1">
        {logs.length === 0 && <Card className="p-8 text-center text-foreground/50">لا سجل بعد.</Card>}
        {logs.map((l, i) => {
          const twilioState = l.read_at ? "read" : l.delivered_at ? "delivered" : (l.status === "sent" ? "sent" : null);
          return (
            <Card key={i} className="p-3 text-xs" data-testid={`wa-log-row-${i}`}>
              <div className="flex justify-between gap-2 flex-wrap items-center">
                <a href={`/admin/journey?phone=${encodeURIComponent(l.to)}`} className="font-mono hover:underline" dir="ltr">{l.to}</a>
                <span className="text-foreground/60">{l.template_key}</span>
                <Badge variant="secondary" className="text-[10px]">{l.status}</Badge>
                {twilioState && chip(twilioState)}
                {l.attribution_token && <span className="text-[10px] text-primary">🎯</span>}
                <span className="text-foreground/50 text-[10px]">{new Date(l.sent_at).toLocaleString("ar")}</span>
              </div>
              {l.body_snapshot && <div className="text-foreground/70 mt-1 truncate">{l.body_snapshot}</div>}
              {l.error && <div className="text-red-700 mt-1 truncate">خطأ: {l.error}</div>}
              {l.delivered_at && (
                <div className="text-[10px] text-foreground/40 mt-0.5">
                  وصلت: {new Date(l.delivered_at).toLocaleString("ar")}
                  {l.read_at && <> · قُرئت: {new Date(l.read_at).toLocaleString("ar")}</>}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function HealthCell({ label, value, color }) {
  return (
    <Card className={`p-2 border ${color}`}>
      <div className="text-[10px] opacity-80">{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </Card>
  );
}

// ---------------- Unsubscribes ----------------
function WAUnsubs() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/whatsapp/unsubscribes").then((r) => setItems(r.data)); }, []);
  return (
    <div className="space-y-3 pt-2">
      <p className="text-sm text-foreground/60">أرقام قامت بالرد بـ STOP أو إلغاء — لن تُرسل لهم رسائل مستقبلًا.</p>
      {items.length === 0 && <Card className="p-8 text-center text-foreground/50">لا يوجد.</Card>}
      {items.map((u, i) => (
        <Card key={i} className="p-3 text-sm flex justify-between">
          <span className="font-mono" dir="ltr">+{u.number}</span>
          <span className="text-foreground/50 text-xs">{u.reason} • {new Date(u.created_at).toLocaleDateString("ar")}</span>
        </Card>
      ))}
    </div>
  );
}

function MiniStat({ label, value, color = "text-foreground" }) {
  return (
    <div className="border rounded-md p-3 bg-white">
      <div className="text-[10px] text-foreground/60">{label}</div>
      <div className={`text-xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

// ---------------- Inbox (Conversations) ----------------
function WAInbox() {
  const [conversations, setConversations] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/inbox");
      setConversations(data);
    } finally { setLoading(false); }
  };

  const openConversation = async (num) => {
    setSelectedNumber(num);
    setMessages([]);
    const { data } = await api.get(`/whatsapp/inbox/${encodeURIComponent(num)}`);
    setMessages(data.messages || []);
    loadConversations(); // refresh unread counts
  };

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/whatsapp/inbox/${encodeURIComponent(selectedNumber)}/reply`, { body: reply });
      setReply("");
      toast.success("أُرسلت");
      openConversation(selectedNumber);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الإرسال");
    } finally { setSending(false); }
  };

  // Voice-note reply — Twilio requires a public MediaUrl.  We upload the
  // recording to Cloudflare R2 first (via /uploads?kind=audio), then hand the
  // URL to the backend which passes it into Twilio's Messages.create as
  // media_url. Twilio transcodes on their side so we don't need ffmpeg here.
  const sendVoice = async ({ url, duration_sec, content_type }) => {
    if (!selectedNumber) return;
    setSending(true);
    try {
      await api.post(`/whatsapp/inbox/${encodeURIComponent(selectedNumber)}/reply`, {
        body: "",
        media_url: url,
        media_type: content_type || "audio/ogg",
      });
      toast.success(`أُرسلت رسالة صوتية (${duration_sec}ث)`);
      openConversation(selectedNumber);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الإرسال");
    } finally { setSending(false); }
  };

  useEffect(() => { loadConversations(); }, []);
  // Auto-refresh every 15s while looking at inbox
  useEffect(() => {
    const iv = setInterval(loadConversations, 15000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="pt-2">
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 mb-3">
        💬 يمكنك الرد على أي عميل خلال 24 ساعة من آخر رسالة أرسلها لك — بعد ذلك يجب استخدام قالب معتمد من Meta.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 min-h-[500px]">
        {/* Conversation List */}
        <Card className="p-0 overflow-hidden md:col-span-1" data-testid="wa-inbox-list">
          <div className="p-3 border-b font-semibold text-sm flex items-center justify-between">
            <span>المحادثات ({conversations.length})</span>
            <Button size="sm" variant="ghost" onClick={loadConversations}><Search className="h-3 w-3" /></Button>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {loading && <p className="p-4 text-sm text-foreground/50 text-center">جاري التحميل...</p>}
            {!loading && conversations.length === 0 && (
              <p className="p-8 text-sm text-foreground/50 text-center">لا توجد محادثات بعد.<br/>ستظهر هنا عند رد العملاء على رسائلك.</p>
            )}
            {conversations.map((c) => (
              <button
                key={c.number}
                onClick={() => openConversation(c.number)}
                className={`w-full text-right p-3 border-b hover:bg-foreground/5 transition ${selectedNumber === c.number ? "bg-green-50" : "bg-white"}`}
                data-testid={`wa-conv-${c.number}`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">
                      {c.contact_name || <span className="text-foreground/60">مجهول</span>}
                    </div>
                    <div className="font-mono text-[10px] text-foreground/50" dir="ltr">+{c.number}</div>
                    <div className="text-xs text-foreground/70 truncate mt-1">
                      {c.last_direction === "out" && <span className="text-foreground/40">أنت: </span>}
                      {c.last_message}
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-end gap-1">
                    <span className="text-[10px] text-foreground/50">
                      {c.last_at ? new Date(c.last_at).toLocaleString("ar", { dateStyle: "short", timeStyle: "short" }) : ""}
                    </span>
                    {c.unread_count > 0 && (
                      <span className="bg-green-600 text-white rounded-full text-[10px] px-2 py-0.5 min-w-[20px] text-center">
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Conversation Thread */}
        <Card className="p-0 overflow-hidden md:col-span-2 flex flex-col" data-testid="wa-inbox-thread">
          {!selectedNumber ? (
            <div className="flex-1 flex items-center justify-center text-foreground/40 text-sm p-8">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>اختر محادثة من القائمة لعرضها والرد عليها</p>
              </div>
            </div>
          ) : (
            <>
              <div className="p-3 border-b bg-black/[0.02] flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-green-700" />
                <span className="font-mono text-sm" dir="ltr">+{selectedNumber}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#EFEAE2] min-h-[400px] max-h-[500px]">
                {messages.length === 0 && <p className="text-foreground/40 text-center text-sm">لا رسائل بعد.</p>}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg p-2 text-sm ${m.direction === "out" ? "bg-[#DCF8C6] rounded-br-none" : "bg-white rounded-bl-none"}`}>
                      {/* Inline media — voice notes get a compact player, images inline, everything else as a download link */}
                      {m.media?.map((a, idx) => {
                        const url = a.url || "";
                        const ct = a.content_type || "";
                        if (ct.startsWith("audio/")) {
                          return <VoicePlayer key={idx} url={url} testid={`wa-voice-${i}-${idx}`} />;
                        }
                        if (ct.startsWith("image/")) {
                          return <img key={idx} src={url} alt="" className="max-h-56 rounded-md mb-1" />;
                        }
                        return <a key={idx} href={url} target="_blank" rel="noreferrer" className="block underline text-xs mb-1 break-all">📎 ملف</a>;
                      })}
                      {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                      <div className="flex items-center gap-1 justify-end mt-1 text-[10px] text-foreground/50">
                        {m.template_key && <Badge variant="secondary" className="text-[9px] px-1 py-0">{m.template_key}</Badge>}
                        <span>{m.at ? new Date(m.at).toLocaleTimeString("ar", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                        {m.direction === "out" && m.status && (
                          <span className={m.status === "delivered" || m.status === "read" ? "text-blue-600" : ""}>
                            {m.status === "read" ? "✓✓" : m.status === "delivered" ? "✓✓" : "✓"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t bg-white flex gap-2 items-center">
                <VoiceRecorder onSent={sendVoice} disabled={sending} />
                <Input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="اكتب ردًا أو سجّل رسالة صوتية… (خلال 24س من آخر رسالة للعميل)"
                  dir="rtl"
                  className="flex-1"
                  data-testid="wa-reply-input"
                />
                <Button onClick={send} disabled={sending || !reply.trim()} data-testid="wa-reply-send">
                  {sending ? "..." : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---------------- Automations (Sequences) ----------------
function WAAutomations() {
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [enrollments, setEnrollments] = useState(null);

  const load = async () => {
    const [s, t] = await Promise.all([
      api.get("/whatsapp/sequences"),
      api.get("/whatsapp/templates"),
    ]);
    setSequences(s.data);
    setTemplates(t.data.filter((x) => !x.disabled));
  };
  useEffect(() => { load(); }, []);

  const newSequence = () => setEditing({
    _isNew: true,
    name: "",
    description: "",
    trigger: "lead_no_purchase",
    _funnel: "all",
    enabled: true,
    steps: [{ template_key: templates[0]?.key || "", delay_minutes: 5, variables_map: { "1": "full_name" }, static_variables: {} }],
  });

  const openEnrollments = async (seqId) => {
    const { data } = await api.get(`/whatsapp/sequences/${seqId}/enrollments`);
    setEnrollments({ seqId, list: data });
  };

  const del = async (seqId) => {
    if (!confirm("حذف التلقائية؟ ستُلغى كل التسجيلات النشطة.")) return;
    await api.delete(`/whatsapp/sequences/${seqId}`);
    toast.success("حُذفت");
    load();
  };

  return (
    <div className="space-y-3 pt-2">
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
        <div className="flex items-start gap-2">
          <Zap className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-1">كيف تعمل التلقائيات؟</div>
            كل عميل محتمل (Lead) جديد يُسجَّل تلقائيًا في التلقائيات المُفعّلة. يتم إرسال كل خطوة حسب زمن الانتظار الذي تحدده.
            <strong> بمجرد أن يُكمل العميل الشراء، يتم إلغاء جميع الخطوات المتبقية تلقائيًا</strong> — لن يستقبل رسائل مزعجة.
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-sm text-foreground/60">تسلسلات آلية تُرسَل للعملاء المحتملين بعد التسجيل.</p>
        <Button onClick={newSequence} data-testid="wa-new-automation"><Plus className="h-4 w-4 me-1" />تلقائية جديدة</Button>
      </div>

      {sequences.length === 0 && <Card className="p-8 text-center text-foreground/50">لم يتم إنشاء أي تلقائية بعد.</Card>}

      {sequences.map((s) => {
        const [_base, sFunnel] = (s.trigger || "lead_no_purchase").split(":");
        const funnelLabel = { flex: "المرونة", homeworkout: "التمارين المنزلية" }[sFunnel] || "كل الفنلات";
        return (
        <Card key={s.id} className="p-4 space-y-2" data-testid={`wa-seq-${s.id}`}>
          <div className="flex justify-between flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{s.name}</span>
                <Badge variant={s.enabled ? "default" : "secondary"} className="text-[10px]">
                  {s.enabled ? "مُفعّلة" : "متوقفة"}
                </Badge>
                <Badge variant="outline" className="text-[10px]">{s.steps?.length || 0} خطوة</Badge>
                <Badge variant="outline" className="text-[10px] bg-orange-50 border-orange-300 text-orange-800" data-testid={`wa-seq-funnel-${s.id}`}>
                  {funnelLabel}
                </Badge>
              </div>
              <div className="text-xs text-foreground/60 mt-1">{s.description}</div>
              <div className="flex gap-3 text-xs mt-2">
                <span className="text-green-700"><Users className="h-3 w-3 inline" /> {s.active_enrollments} نشط</span>
                <span className="text-blue-700"><CheckCircle2 className="h-3 w-3 inline" /> {s.completed_enrollments} مكتمل</span>
                <span className="text-red-700"><XCircle className="h-3 w-3 inline" /> {s.cancelled_enrollments} ملغى (اشتروا)</span>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Button size="sm" variant="outline" onClick={() => {
                const [base, funnel] = (s.trigger || "lead_no_purchase").split(":");
                setEditing({ ...s, _isNew: false, trigger: base || "lead_no_purchase", _funnel: funnel || "all" });
              }} data-testid={`wa-seq-edit-${s.id}`}>تعديل</Button>
              <Button size="sm" variant="outline" onClick={() => openEnrollments(s.id)} data-testid={`wa-seq-enrollments-${s.id}`}>التسجيلات</Button>
              <Button size="sm" variant="ghost" onClick={() => del(s.id)} data-testid={`wa-seq-del-${s.id}`}><Trash2 className="h-3 w-3 text-red-600" /></Button>
            </div>
          </div>
          {/* Step preview */}
          <div className="border-t pt-2 mt-2 space-y-1">
            {s.steps?.map((step, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="h-5 w-5 rounded-full bg-foreground/10 grid place-items-center font-semibold">{i + 1}</span>
                <Clock className="h-3 w-3 text-foreground/50" />
                <span className="font-mono">{formatDelay(step.delay_minutes)}</span>
                <span>→</span>
                <Badge variant="secondary" className="text-[10px]">{step.template_key}</Badge>
              </div>
            ))}
          </div>
        </Card>
        );
      })}

      <SequenceEditor
        editing={editing}
        setEditing={setEditing}
        templates={templates}
        onSaved={() => { setEditing(null); load(); }}
      />

      <EnrollmentsDialog
        enrollments={enrollments}
        onClose={() => setEnrollments(null)}
      />
    </div>
  );
}

function formatDelay(minutes) {
  if (minutes < 60) return `${minutes} دقيقة`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60 * 10) / 10} ساعة`;
  return `${Math.round(minutes / (60 * 24) * 10) / 10} يوم`;
}

function SequenceEditor({ editing, setEditing, templates, onSaved }) {
  const save = async () => {
    if (!editing.name) return toast.error("أدخل اسم التلقائية");
    if (editing.steps.length === 0) return toast.error("أضف خطوة واحدة على الأقل");
    for (const s of editing.steps) {
      if (!s.template_key) return toast.error("اختر قالبًا لكل خطوة");
    }
    try {
      const funnel = editing._funnel || "all";
      const baseTrigger = editing.trigger || "lead_no_purchase";
      const compoundTrigger = funnel === "all" ? baseTrigger : `${baseTrigger}:${funnel}`;
      const payload = {
        name: editing.name,
        description: editing.description,
        trigger: compoundTrigger,
        enabled: editing.enabled,
        steps: editing.steps.map((s) => ({
          template_key: s.template_key,
          delay_minutes: parseInt(s.delay_minutes) || 0,
          variables_map: s.variables_map || {},
          static_variables: s.static_variables || {},
        })),
      };
      if (editing._isNew) {
        await api.post("/whatsapp/sequences", payload);
      } else {
        await api.put(`/whatsapp/sequences/${editing.id}`, payload);
      }
      toast.success("تم الحفظ");
      onSaved();
    } catch (err) {
      toast.error("فشل الحفظ: " + (err?.response?.data?.detail || "خطأ"));
    }
  };

  const addStep = () => {
    setEditing({
      ...editing,
      steps: [...editing.steps, { template_key: templates[0]?.key || "", delay_minutes: 60, variables_map: { "1": "full_name" }, static_variables: {} }],
    });
  };
  const removeStep = (i) => setEditing({ ...editing, steps: editing.steps.filter((_, idx) => idx !== i) });
  const updateStep = (i, patch) => setEditing({ ...editing, steps: editing.steps.map((s, idx) => idx === i ? { ...s, ...patch } : s) });

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
        {editing && <>
          <DialogHeader><DialogTitle>{editing._isNew ? "تلقائية جديدة" : `تعديل: ${editing.name}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">اسم التلقائية</label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="wa-seq-name" />
            </div>
            <div>
              <label className="text-xs font-medium">الوصف</label>
              <Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} data-testid="wa-seq-enabled" />
              مُفعّلة (جميع العملاء الجدد سيُسجَّلون تلقائيًا)
            </label>

            <div>
              <label className="text-xs font-medium">الفنل التسويقي</label>
              <select
                value={editing._funnel || "all"}
                onChange={(e) => setEditing({ ...editing, _funnel: e.target.value })}
                className="w-full border rounded-md p-2 text-sm bg-white"
                data-testid="wa-seq-funnel"
              >
                <option value="all">كل الفنلات (افتراضي)</option>
                <option value="flex">المرونة (/flex)</option>
                <option value="homeworkout">التمارين المنزلية (/homeworkout)</option>
              </select>
              <div className="text-[10px] text-foreground/50 mt-1">
                اختر فنل معيّن لاستهداف زوّار صفحة هبوط محدّدة فقط. "كل الفنلات" يعمل كافتراضي لو ما فيه تلقائية مخصّصة للفنل.
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium">الخطوات</label>
                <Button size="sm" variant="outline" onClick={addStep} data-testid="wa-seq-add-step"><Plus className="h-3 w-3 me-1" />خطوة</Button>
              </div>
              <div className="space-y-2">
                {editing.steps.map((step, i) => (
                  <Card key={i} className="p-3 space-y-2 bg-black/[0.02]" data-testid={`wa-seq-step-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold">الخطوة {i + 1}</span>
                      <Button size="sm" variant="ghost" onClick={() => removeStep(i)}><Trash2 className="h-3 w-3 text-red-600" /></Button>
                    </div>
                    <div>
                      <label className="text-[10px] text-foreground/60">القالب</label>
                      <select
                        value={step.template_key}
                        onChange={(e) => updateStep(i, { template_key: e.target.value })}
                        className="w-full border rounded-md p-2 text-sm bg-white"
                        data-testid={`wa-seq-step-${i}-template`}
                      >
                        <option value="">— اختر —</option>
                        {templates.map((t) => <option key={t.key} value={t.key}>{t.name} ({t.key})</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-foreground/60">
                        زمن الانتظار (بالدقائق) — {i === 0 ? "بعد تسجيل العميل المحتمل" : "بعد الخطوة السابقة"}
                      </label>
                      <div className="flex gap-2 flex-wrap items-center">
                        <Input
                          type="number" min={0}
                          value={step.delay_minutes}
                          onChange={(e) => updateStep(i, { delay_minutes: parseInt(e.target.value) || 0 })}
                          className="w-24"
                          data-testid={`wa-seq-step-${i}-delay`}
                        />
                        <span className="text-xs text-foreground/60">= {formatDelay(step.delay_minutes)}</span>
                        {[5, 60, 600, 1440, 4320].map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => updateStep(i, { delay_minutes: m })}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-foreground/20 hover:bg-foreground/5"
                          >
                            {formatDelay(m)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <details className="text-xs">
                      <summary className="cursor-pointer text-foreground/60">ربط المتغيرات (متقدم)</summary>
                      <div className="mt-2 space-y-2">
                        <div>
                          <label className="text-[10px] text-foreground/60">ربط ديناميكي (JSON)</label>
                          <textarea
                            value={JSON.stringify(step.variables_map || {}, null, 2)}
                            onChange={(e) => { try { updateStep(i, { variables_map: JSON.parse(e.target.value) }); } catch {} }}
                            className="w-full border rounded-md p-2 font-mono text-[10px] min-h-[50px]"
                            dir="ltr"
                          />
                          <p className="text-[10px] text-foreground/50">
                            القيم المسموحة: <code className="font-mono bg-muted px-1 rounded">full_name</code>،{" "}
                            <code className="font-mono bg-muted px-1 rounded">email</code>،{" "}
                            <code className="font-mono bg-muted px-1 rounded">checkout_link</code> (رابط كامل)،{" "}
                            <code className="font-mono bg-muted px-1 rounded">checkout_token</code> (فقط التوكن — استخدمه لما قالب Twilio عنده URL button بصيغة{" "}
                            <span dir="ltr">https://faisalfit.com/r/{"{"}{"{"}<span className="text-orange-700">3</span>{"}"}{"}"}</span>)
                          </p>
                        </div>
                        <div>
                          <label className="text-[10px] text-foreground/60">قيم ثابتة (JSON)</label>
                          <textarea
                            value={JSON.stringify(step.static_variables || {}, null, 2)}
                            onChange={(e) => { try { updateStep(i, { static_variables: JSON.parse(e.target.value) }); } catch {} }}
                            className="w-full border rounded-md p-2 font-mono text-[10px] min-h-[50px]"
                            dir="ltr"
                          />
                        </div>
                      </div>
                    </details>
                  </Card>
                ))}
              </div>
            </div>

            <Button onClick={save} className="w-full" data-testid="wa-seq-save">حفظ التلقائية</Button>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}

function EnrollmentsDialog({ enrollments, onClose }) {
  const cancel = async (enrId) => {
    if (!confirm("إلغاء تسجيل هذا العميل من التلقائية؟")) return;
    await api.post(`/whatsapp/sequences/${enrollments.seqId}/enrollments/${enrId}/cancel`);
    toast.success("تم الإلغاء");
    onClose();
  };
  return (
    <Dialog open={!!enrollments} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" dir="rtl">
        {enrollments && <>
          <DialogHeader><DialogTitle>التسجيلات ({enrollments.list.length})</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {enrollments.list.length === 0 && <p className="text-foreground/50 text-sm p-4 text-center">لا توجد تسجيلات بعد.</p>}
            {enrollments.list.map((e) => (
              <Card key={e.id} className="p-2 text-xs">
                <div className="flex justify-between items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono truncate">{e.lead_email}</div>
                    <div className="text-foreground/50 text-[10px]">
                      خطوة {e.current_step + 1}/{e.total_steps} • {e.status === "active" ? `التالي: ${new Date(e.next_run_at).toLocaleString("ar", { dateStyle: "short", timeStyle: "short" })}` : e.status}
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-[10px] flex-shrink-0">{e.status}</Badge>
                  {e.status === "active" && (
                    <Button size="sm" variant="ghost" onClick={() => cancel(e.id)}><XCircle className="h-3 w-3 text-red-600" /></Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}
